import { Loader2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import {
  countryCodes,
  countryLabel,
  ProjectBasicsSchema,
  type CountryCode,
  type ParsedPhase,
  type Project,
  type ProjectSuggestion,
} from "@gb/contracts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateProject, useSetSetupStep, useUpdateProject } from "../api";
import { documentRefusal, useUploadPlan } from "../documents-api";
import { useParsePlan, useReplacePhases } from "../phases-api";
import { Field } from "@/components/gb/field";
import { STEP, StepPanel, WizardFooter, type StepProps } from "./wizard";

type Errors = Partial<Record<"name" | "country", string>>;

/** Which of the four fields the plan filled, so each can say where it came from. */
type BasicsField = "name" | "country" | "region" | "spec";
type FilledFrom = { fields: Set<BasicsField>; filename: string };

/**
 * A filename reduced to something usable as a project name.
 *
 * Only ever a placeholder: the row has to be created the instant a file is
 * chosen, and `name` is the one column that is still `NOT NULL`. "Untitled"
 * would be worse — a user who uploads and then abandons should leave behind a
 * row they can recognise in the picker, and `ashfield-programme-rev-c` is at
 * least theirs. The parse overwrites it moments later if the document says
 * anything better.
 */
function nameFromFilename(filename: string): string {
  const stem = filename.replace(/\.[^.]+$/, "");
  return stem.replace(/[_-]+/g, " ").trim().slice(0, 160) || "Untitled project";
}

/**
 * Step 1 — the only step that gates anything.
 *
 * Form state is plain `useState` plus the shared Zod schema rather than a form
 * library: nothing is in `package.json` today, and one required field and one
 * enum do not justify the dependency. Validating with `ProjectBasicsSchema`
 * means the message the user sees here is the same rule the server enforces.
 *
 * The plan upload lives here rather than on step 2, because a programme names
 * the project it is a programme for and this is the step asking for that. It
 * carries a cost worth stating: uploading requires somewhere to put the bytes,
 * so **choosing a file creates the project**, and a user who then walks away
 * leaves a `setup-incomplete` row in the picker. That was the accepted trade —
 * the alternative was a second, project-less upload path existing solely for
 * this one step.
 */
export function StepBasics({ project, mode, goTo, onCreated }: StepProps) {
  const [name, setName] = useState(project?.name ?? "");
  const [country, setCountry] = useState<CountryCode | "">(project?.country ?? "");
  const [region, setRegion] = useState(project?.region ?? "");
  const [spec, setSpec] = useState(project?.spec ?? "");
  const [errors, setErrors] = useState<Errors>({});

  /**
   * The project this step created, when it arrived without one.
   *
   * Held here rather than navigating to `/projects/:id/setup/1` the moment it
   * exists: that route change unmounts this component, and it would happen
   * while the parse is still running — throwing away the file, the in-flight
   * request and everything typed so far. The URL moves on Continue, as it
   * always did.
   */
  const [created, setCreated] = useState<Project | null>(null);
  const current = project ?? created;

  /** Phases from the plan, written through on Continue once a project exists. */
  const [phases, setPhases] = useState<ParsedPhase[]>([]);
  const [filled, setFilled] = useState<FilledFrom | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planNote, setPlanNote] = useState<string | null>(null);

  /**
   * Fields the user has typed into, which a parse must never overwrite.
   *
   * Tracked as "touched" rather than "non-empty" deliberately. A user who
   * clears a box has expressed something, and a later parse silently refilling
   * it would be the same surprise as overwriting a typed value.
   */
  const touched = useRef(new Set<string>());
  const fileRef = useRef<HTMLInputElement>(null);

  const create = useCreateProject();
  const update = useUpdateProject();
  const setStep = useSetSetupStep();
  const uploadPlan = useUploadPlan();
  const parsePlan = useParsePlan();
  const replacePhases = useReplacePhases(current?.id ?? "");

  const busy =
    create.isPending || update.isPending || setStep.isPending || replacePhases.isPending;
  const planBusy = uploadPlan.isPending || parsePlan.isPending || create.isPending;
  const failure = create.error ?? update.error ?? setStep.error ?? replacePhases.error;

  const mark = (field: string) => (): void => void touched.current.add(field);

  /**
   * Apply a suggestion to the fields nobody has typed into.
   *
   * Pre-fill, never overwrite — the rule
   * `project_intelligence/document-ai/0002` sets for a scanned certificate, and
   * the same reasoning applies: what the model read is a proposal, and the
   * confirmation is the user pressing Continue with it on screen.
   */
  const applySuggestion = (suggestion: ProjectSuggestion, filename: string): void => {
    const fields = new Set<BasicsField>();

    if (suggestion.name && !touched.current.has("name") && !name.trim()) {
      setName(suggestion.name);
      fields.add("name");
    }
    if (suggestion.country && !touched.current.has("country") && !country) {
      setCountry(suggestion.country);
      fields.add("country");
    }
    if (suggestion.region && !touched.current.has("region") && !region.trim()) {
      setRegion(suggestion.region);
      fields.add("region");
    }
    if (suggestion.spec && !touched.current.has("spec") && !spec.trim()) {
      setSpec(suggestion.spec);
      fields.add("spec");
    }

    setFilled(fields.size > 0 ? { fields, filename } : null);
  };

  /**
   * Choose a plan: create the project if there isn't one, store the file, read it.
   *
   * The order is the same hard requirement step 2 has always had — the document
   * is stored *before* anything tries to parse it, so an unreadable programme
   * still leaves the file on the project and in Brain → Sources. What is new is
   * the create in front, which is what gives the upload somewhere to go.
   */
  const onPlanFile = async (file: File): Promise<void> => {
    setPlanError(null);
    setPlanNote(null);

    // Checked before the project is created (`architecture/0031`). An empty
    // or oversized plan used to create the project and then fail, leaving a
    // project named after a file that was never stored.
    const refused = documentRefusal(file);
    if (refused) {
      setPlanError(refused);
      return;
    }

    try {
      // `current` is captured before the awaits: `created` will not have
      // re-rendered by the time the upload needs an id.
      let target = current;
      if (!target) {
        target = await create.mutateAsync({
          name: name.trim() || nameFromFilename(file.name),
          ...(country ? { country } : {}),
          ...(region.trim() ? { region: region.trim() } : {}),
          ...(spec.trim() ? { spec: spec.trim() } : {}),
        });
        setCreated(target);
      }

      const document = await uploadPlan.mutateAsync({ projectId: target.id, file });
      const result = await parsePlan.mutateAsync({
        projectId: target.id,
        documentId: document.id,
      });

      if (result.suggestion?.attempted === false) {
        // Nothing read it — a deployment with no model key. Saying "nothing
        // found" here would blame a document that was never opened.
        setPlanNote(
          "Saved to the project. No document reader is configured, so nothing was filled in.",
        );
        return;
      }

      if (result.suggestion) applySuggestion(result.suggestion, document.filename);
      setPhases(result.phases);

      if (result.phases.length === 0 && !result.suggestion?.name) {
        setPlanNote(
          "Nothing recognisable in that file — fill this in by hand. The document is saved either way.",
        );
      } else if (result.phases.length > 0) {
        setPlanNote(
          `${result.phases.length} ${result.phases.length === 1 ? "phase" : "phases"} found — you'll see them on the next step.`,
        );
      }
    } catch (error) {
      setPlanError(error instanceof Error ? error.message : "Couldn’t read that plan.");
    }
  };

  /**
   * Write the fields as they stand. Null means they did not validate and
   * nothing was sent.
   */
  const persist = async (): Promise<Project | null> => {
    const parsed = ProjectBasicsSchema.safeParse({
      name,
      // Omitted rather than sent as "" — `country` is nullable now, and the
      // schema's enum has no member for "not chosen".
      ...(country ? { country } : {}),
      // Empty optional fields are omitted, not sent as "" — the column is
      // nullable and an empty string is not the same as "not given".
      ...(region.trim() ? { region: region.trim() } : {}),
      ...(spec.trim() ? { spec: spec.trim() } : {}),
    });

    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === "name" || key === "country") next[key] = issue.message;
      }
      setErrors(next);
      return null;
    }
    setErrors({});

    // First pass creates the project; every pass after that patches it, so
    // going Back to step 1 and editing does not strand a second row — and so
    // the row a plan upload already created is updated rather than duplicated.
    return current
      ? await update.mutateAsync({ id: current.id, patch: parsed.data })
      : await create.mutateAsync(parsed.data);
  };

  const submit = async (): Promise<void> => {
    const saved = await persist();
    if (!saved) return;

    // Phases the plan proposed, written now that a project certainly exists.
    // Step 2 then hydrates them from the server exactly as it does for a
    // programme uploaded there — it needs no knowledge of this path.
    if (phases.length > 0) {
      await replacePhases.mutateAsync(
        phases.map((phase) => ({
          level: phase.level,
          name: phase.name,
          startDate: phase.startDate ?? null,
          endDate: phase.endDate ?? null,
          note: phase.note ?? null,
          origin: "parsed" as const,
          editedByUser: false,
        })),
      );
      setPhases([]);
    }

    // Not in edit mode. `setSetupStep` keeps `max(stored, incoming)` and a live
    // project is already at the last step, so the call is a no-op round trip
    // that claims the wizard is moving a cursor it is not.
    if (mode === "setup") await setStep.mutateAsync({ id: saved.id, step: STEP.milestones });

    // On create the caller has to move the URL into the new project — step 1
    // runs at `/projects/new`, which has no id to build the next path from.
    // `project` rather than `current`: a project this step created itself still
    // needs the URL moved.
    if (!project && onCreated) {
      onCreated(saved);
      return;
    }
    goTo(STEP.milestones);
  };

  /**
   * In edit mode Back commits first, matching steps 2 and 4.
   *
   * In setup mode this control is Cancel and writes nothing, which is right
   * when the project does not exist yet and wrong once it does — leaving step 1
   * was the one place in the flow where an edit could vanish with nothing said.
   *
   * Invalid input navigates anyway rather than trapping the user, as on
   * Compliance.
   * The only field that can fail here is the required name, and a blank name is
   * not an edit anyone would expect to be kept.
   */
  const back = async (): Promise<void> => {
    if (mode === "edit") await persist();
    goTo(0);
  };

  const fromPlan = (field: string): boolean => filled?.fields.has(field as never) ?? false;

  return (
    <>
      <StepPanel
        title="Project basics"
        description="Name, location and the dates the programme runs between."
      >
        <div className="flex flex-col gap-7">
          {/* Above the fields, not below them: this is the thing that fills
              them in, and offering it after the user has typed all four is
              offering it too late to be worth anything. */}
          <section>
            <div className="flex flex-wrap items-center gap-4 rounded-card border-2 border-dashed border-border bg-surface-sunken px-5 py-4">
              <span className="flex shrink-0 text-muted">
                <Upload size={24} aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[1rem] font-bold text-ink">
                  Have a programme or phased plan? Upload it and we’ll fill this in.
                </p>
                <p className="mt-0.5 text-[0.84rem] text-muted">
                  PDF / DOCX / XLSX — name, location and phases get read out of it
                </p>
              </div>
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={planBusy}
                className="shrink-0 hover:bg-brand-deep"
              >
                {planBusy ? <Loader2 size={16} className="animate-spin" /> : null}
                {planBusy ? "Reading plan…" : "Choose file"}
              </Button>
              <input
                ref={fileRef}
                type="file"
                className="sr-only"
                accept=".pdf,.docx,.xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) void onPlanFile(file);
                }}
              />
            </div>
            {(planNote ?? planError) && (
              <p
                role="status"
                className="mt-2.5 rounded-input border-[1.5px] border-border bg-surface px-4 py-3 text-[0.83rem] text-ink-slate"
              >
                {planError ?? planNote}
              </p>
            )}
          </section>

          <div className="grid gap-[18px] sm:grid-cols-2">
            <Field
              label="Project name"
              error={errors.name}
              hint={fromPlan("name") ? `From ${filled?.filename}` : undefined}
              className="sm:col-span-2"
            >
              {(props) => (
                <Input
                  {...props}
                  value={name}
                  placeholder="Riverside Solar Plant"
                  maxLength={160}
                  onChange={(e) => {
                    mark("name")();
                    setName(e.target.value);
                  }}
                />
              )}
            </Field>

            <Field
              label="Country"
              error={errors.country}
              hint={
                fromPlan("country")
                  ? `From ${filled?.filename}`
                  : "Determines which compliance regime applies."
              }
            >
              {({ id, ...props }) => (
                <Select
                  value={country || undefined}
                  onValueChange={(value) => {
                    mark("country")();
                    setCountry(value as CountryCode);
                  }}
                >
                  <SelectTrigger id={id} {...props}>
                    <SelectValue placeholder="Choose a country" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Every country of ISO 3166-1, by its name (LANG-11). */}
                    {countryCodes().map((code) => (
                      <SelectItem key={code} value={code}>
                        {countryLabel(code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field
              label="Region"
              optional
              hint={fromPlan("region") ? `From ${filled?.filename}` : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  value={region}
                  placeholder="Merseyside"
                  maxLength={120}
                  onChange={(e) => {
                    mark("region")();
                    setRegion(e.target.value);
                  }}
                />
              )}
            </Field>

            <Field
              label="Capacity / spec"
              optional
              className="sm:col-span-2"
              hint={fromPlan("spec") ? `From ${filled?.filename}` : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  value={spec}
                  placeholder="12 MW ground-mount"
                  maxLength={160}
                  onChange={(e) => {
                    mark("spec")();
                    setSpec(e.target.value);
                  }}
                />
              )}
            </Field>
          </div>
        </div>
      </StepPanel>

      <WizardFooter
        onBack={() => void back()}
        backLabel={mode === "edit" ? "Back to project" : "Cancel"}
        onContinue={() => void submit()}
        // Disabled only on the genuinely empty case; everything else reports
        // inline once the user tries, rather than leaving a dead button with
        // no explanation.
        continueDisabled={!name.trim()}
        busy={busy}
        error={failure instanceof Error ? failure.message : null}
      />
    </>
  );
}
